import { useMemo } from "react"
import { useAuth } from "@/contexts/auth-context"
import { env } from "@/lib/env"
import { getBasePathUrl } from "@/lib/base-path"
import { requestTokenDueToAuthFailure } from "@/lib/app-messaging"
import { tokenManager } from "@/lib/token-manager"

export const API_ENDPOINTS = {
  PHOENIX: {
    BASE_URL: env.apiUrl,
  },
  REPORTS: {
    BASE_URL: env.reportsApiUrl,
  },
}

export function useApi() {
  const { token } = useAuth()

  const fetchWithAuth = async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
    const headers = new Headers(options.headers)

    if (token) {
      headers.set("Authorization", `Bearer ${token}`)
    }

    let finalUrl = url
    if (!url.startsWith("http") && !url.startsWith("//")) {
      finalUrl = getBasePathUrl(url)
    }

    try {
      const response = await fetch(finalUrl, { ...options, headers })

      if (!response.ok) {
        if (response.status === 401) {
          if (tokenManager.shouldRequestToken()) {
            requestTokenDueToAuthFailure(`401 Unauthorized from ${finalUrl}`)
          }

          const retriedResponse = await tokenManager.queueRequest(finalUrl, { ...options, headers })

          if (!retriedResponse.ok) {
            const errorData = await retriedResponse.json().catch(() => ({}))
            throw new Error(errorData.message || `API request failed with status ${retriedResponse.status}`)
          }

          const contentType = retriedResponse.headers.get("content-type")
          if (contentType && contentType.includes("application/json")) {
            return retriedResponse.json()
          }
          return retriedResponse as unknown as T
        }

        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `API request failed with status ${response.status}`)
      }

      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        return response.json()
      }
      return response as unknown as T
    } catch (error) {
      throw error
    }
  }

  const get = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "GET" })

  const post = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options.headers },
      body: JSON.stringify(data),
    })

  const del = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "DELETE" })

  const createUrl = (baseUrl: string, path: string) => {
    if (!baseUrl.startsWith("http")) {
      throw new Error(`Invalid baseUrl: ${baseUrl}`)
    }
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
    const cleanPath = path.startsWith("/") ? path.slice(1) : path
    return `${base}/${cleanPath}`
  }

  const phoenixApi = useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.PHOENIX.BASE_URL, path), options),
      post: <T,>(path: string, data: any, options?: RequestInit) =>
        post<T>(createUrl(API_ENDPOINTS.PHOENIX.BASE_URL, path), data, options),
      delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.PHOENIX.BASE_URL, path), options),
    }),
    [token]
  )

  const reportsApi = useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.REPORTS.BASE_URL, path), options),
      post: <T,>(path: string, data: any, options?: RequestInit) =>
        post<T>(createUrl(API_ENDPOINTS.REPORTS.BASE_URL, path), data, options),
      delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.REPORTS.BASE_URL, path), options),
    }),
    [token]
  )

  return {
    phoenixApi,
    reportsApi,
    fetchWithAuth,
  }
}
